import { useCentersList } from '../../api/hooks/Centers/useCentersList'
import SaveButton from '../common/SaveButton'
import CenterCard from './CenterCard'
import { useState } from 'react'
import { Modal, Button, Select, PasswordInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import styles from '../../styles/admin/CenterCard.module.scss'
import { apiRequest } from '../../utils/api'
import { useEffect } from 'react'
import { Icon } from '../../assets/icons'
import { Pagination } from '@mantine/core'

const CenterList = () => {
  const defaultCenterForm = {
    name: '',
    managerName: '',
    managerEmail: '',
    postcode: '',
    address: '',
    phone: '',
    country: '',
    city: '',
    userID: '',
    password: '',
  }

  const { data: requestData, isLoading } = useCentersList()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [selectedCenter, setSelectedCenter] = useState(null)
  const [centerForm, setCenterForm] = useState(defaultCenterForm)
  const [detailForm, setDetailForm] = useState(defaultCenterForm)
  const [countries, setCountries] = useState([])
  const [selectedPhoneCode, setSelectedPhoneCode] = useState('')
  const [selectedDetailPhoneCode, setSelectedDetailPhoneCode] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const resetCenterForm = () => {
    setCenterForm(defaultCenterForm)
    setSelectedPhoneCode('')
  }

  const resetDetailForm = () => {
    setDetailForm(defaultCenterForm)
    setSelectedDetailPhoneCode('')
  }

  useEffect(() => {
    fetchCountries()
  }, [])

  const fetchCountries = async () => {
    try {
      const res = await apiRequest('/getCountry', 'POST')
      if (res.data) {
        setCountries(res.data)
      }
    } catch (err) {
      console.error('국가 데이터 가져오기 실패:', err)
      notifications.show({
        title: 'Error',
        message: '국가 데이터를 불러오는데 실패했습니다.',
        color: 'red',
      })
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (value) => {
    const numValue = parseInt(value.replace('entries', ''))
    setItemsPerPage(numValue)
    setCurrentPage(1)
  }

  const getCurrentPageData = () => {
    const data = requestData?.data || []
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return data.slice(startIndex, endIndex)
  }

  const totalPages = Math.ceil((requestData?.data?.length || 0) / itemsPerPage)
  const currentData = getCurrentPageData()

  const handleModalOpen = () => {
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    resetCenterForm()
  }

  const handleDetailModalOpen = (centerData) => {
    setSelectedCenter(centerData)
    setDetailForm({
      name: centerData.NAME,
      managerName: centerData.MANAGER_NAME,
      managerEmail: centerData.MANAGER_EMAIL,
      postcode: centerData.POSTCODE || '',
      address: centerData.ADDRESS,
      phone: centerData.PHONE,
      country: centerData.COUNTRY || '',
      city: centerData.CITY || '',
      userID: centerData.USERID || '',
      password: centerData.PASSWORD || '',
    })
    const dialCode = getDialCodeByCountry(centerData.COUNTRY)
    setSelectedDetailPhoneCode(dialCode)

    setIsDetailModalOpen(true)
  }

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false)
    setSelectedCenter(null)
    resetDetailForm()
  }

  const handleAddCenter = async () => {
    if (!centerForm.name.trim()) {
      notifications.show({
        message: 'Please enter the center name.',
        color: 'red',
      })
      return
    }
    if (!centerForm.managerName.trim()) {
      notifications.show({
        message: 'Please enter the manager name.',
        color: 'red',
      })
      return
    }
    if (!centerForm.managerEmail.trim()) {
      notifications.show({
        message: 'Please enter the email.',
        color: 'red',
      })
      return
    }
    if (!centerForm.phone.trim()) {
      notifications.show({
        message: 'Please enter the phone number.',
        color: 'red',
      })
      return
    }
    if (!centerForm.address.trim()) {
      notifications.show({
        message: 'Please enter the address.',
        color: 'red',
      })
      return
    }
    if (!centerForm.country.trim()) {
      notifications.show({
        message: 'Please enter the country.',
        color: 'red',
      })
      return
    }
    if (!centerForm.userID.trim()) {
      notifications.show({
        message: 'Please enter the user ID.',
        color: 'red',
      })
      return
    }
    if (!centerForm.password.trim()) {
      notifications.show({
        message: 'Please enter the password.',
        color: 'red',
      })
      return
    }

    try {
      const centerData = {
        NAME: centerForm.name,
        MANAGER_NAME: centerForm.managerName,
        MANAGER_EMAIL: centerForm.managerEmail,
        POSTCODE: centerForm.postcode,
        ADDRESS: centerForm.address,
        PHONE: centerForm.phone,
        COUNTRY: centerForm.country,
        CITY: centerForm.city,
        USERID: centerForm.userID,
        PASSWORD: centerForm.password,
      }

      const res = await apiRequest('/centers/insert', 'POST', centerData)

      if (res.success) {
        notifications.show({
          title: 'Success',
          message: '센터가 추가되었습니다.',
          color: 'green',
        })

        setIsModalOpen(false)
        resetCenterForm()

        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        notifications.show({
          title: '실패',
          message: '센터 추가에 실패했습니다.',
          color: 'red',
        })
      }
    } catch (err) {
      console.error('센터 추가 중 오류 발생:', err)
      notifications.show({
        title: 'Error',
        message: '센터 추가 중 오류가 발생했습니다.',
        color: 'red',
      })
    }
  }

  const handleUpdateCenter = async () => {
    modals.openConfirmModal({
      title: '센터 정보 수정',
      children: '센터 정보를 수정하시겠습니까?',
      labels: { confirm: '수정', cancel: 'Cancel' },
      confirmProps: { color: 'blue' },
      onConfirm: async () => {
        try {
          const centerData = {
            CENTER_ID: selectedCenter.CENTER_ID,
            NAME: detailForm.name,
            MANAGER_NAME: detailForm.managerName,
            MANAGER_EMAIL: detailForm.managerEmail,
            POSTCODE: detailForm.postcode,
            ADDRESS: detailForm.address,
            PHONE: detailForm.phone,
            COUNTRY: detailForm.country,
            PASSWORD: detailForm.password,
          }

          const res = await apiRequest('/centers/update', 'POST', centerData)

          if (res.success) {
            notifications.show({
              title: 'Success',
              message: '센터 정보가 수정되었습니다.',
              color: 'green',
            })

            setTimeout(() => {
              window.location.reload()
            }, 2000)
            setIsDetailModalOpen(false)
            resetDetailForm()
          } else {
            notifications.show({
              title: '실패',
              message: '센터 수정에 실패했습니다.',
              color: 'red',
            })
          }
        } catch (err) {
          console.error('센터 수정 중 오류 발생:', err)
          notifications.show({
            title: 'Error',
            message: '센터 An error occurred while updating',
            color: 'red',
          })
        }
      },
    })
  }

  const handleDeleteCenter = async () => {
    if (!selectedCenter.CENTER_ID) {
      notifications.show({
        title: 'Error',
        message: '삭제할 항목이 없습니다.',
        color: 'red',
      })
      return
    }

    modals.openConfirmModal({
      title: '센터 삭제',
      children: (
        <div>
          <p>
            정말로 <strong>{selectedCenter.NAME}</strong> 센터를
            삭제하시겠습니까?
          </p>
        </div>
      ),
      labels: { confirm: '삭제', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          const res = await apiRequest('/centers/delete', 'POST', {
            CENTER_ID: selectedCenter.CENTER_ID,
          })

          if (res.success) {
            notifications.show({
              title: 'Success',
              message: '센터가 Deleted.',
              color: 'green',
            })
            setTimeout(() => {
              window.location.reload()
            }, 2000)
            setIsDetailModalOpen(false)
            resetDetailForm()
          } else {
            notifications.show({
              title: '실패',
              message: '센터 삭제에 실패했습니다.',
              color: 'red',
            })
          }
        } catch (err) {
          console.error('센터 삭제 중 오류 발생:', err)
          notifications.show({
            title: 'Error',
            message: '센터 An error occurred while deleting.',
            color: 'red',
          })
        }
      },
    })
  }

  const getDialCodeByCountry = (countryValue) => {
    const selectedCountry = countries.find(
      (country) => country.value === countryValue
    )
    return selectedCountry ? selectedCountry.dial_cd : ''
  }

  const handleSelectCountry = (value) => {
    setCenterForm((prev) => ({
      ...prev,
      country: value,
    }))

    const dialCode = getDialCodeByCountry(value)
    setSelectedPhoneCode(dialCode)
  }

  const handleDetailSelectCountry = (value) => {
    setDetailForm((prev) => ({
      ...prev,
      country: value,
    }))

    const dialCode = getDialCodeByCountry(value)
    setSelectedDetailPhoneCode(dialCode)
  }

  const handleInputChange = (field, value) => {
    setCenterForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDetailInputChange = (field, value) => {
    setDetailForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  if (isLoading) {
    return <div>로딩 중...</div>
  }

  return (
    <div>
      <div className={styles.totalCount}>
        <p>총 {requestData?.data?.length || 0}개의 센터</p>
        <SaveButton onClick={handleModalOpen}>Add Centers</SaveButton>
      </div>

      {currentData.map((center) => (
        <CenterCard
          key={center.CENTER_ID}
          CENTER_ID={center.CENTER_ID}
          NAME={center.NAME}
          MANAGER_NAME={center.MANAGER_NAME}
          MANAGER_EMAIL={center.MANAGER_EMAIL}
          ADDRESS={center.ADDRESS}
          PHONE={center.PHONE}
          REG_DTM={center.REG_DTM}
          onDetailClick={() => handleDetailModalOpen(center)}
        />
      ))}

      <div className={styles.paginationWrapper}>
        <Pagination
          value={currentPage}
          onChange={handlePageChange}
          total={totalPages}
          size="sm"
          withEdges={false}
          color="gray"
          classNames={{
            control: styles.paginationControl,
          }}
        />
      </div>

      <div className="users-select-wrapper">
        <Select
          styles={{ input: { width: '150px', height: '30px' } }}
          data={[
            { value: '10entries', label: '10entries' },
            { value: '20entries', label: '20entries' },
            { value: '50entries', label: '50entries' },
          ]}
          value={`${itemsPerPage}entries`}
          onChange={handleItemsPerPageChange}
        />
      </div>

      <Modal
        opened={isModalOpen}
        onClose={handleCloseModal}
        size="xl"
        styles={{
          content: {
            borderRadius: '15px',
          },
          close: {
            top: '-20px',
            right: '10px',
          },
        }}
      >
        <div className="modal-content">
          <div className="top-wrapper">
            <div className="level-info-wrapper">
              <div className="level-header-wrapper">
                <Icon name="info" size={20} />
                <p className="text-lg">담당자 정보</p>
              </div>

              <div className="info-inputs-wrapper">
                <div className="field-item">
                  <p className="text-sm input-label">센터명</p>
                  <input
                    type="text"
                    value={centerForm.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="센터명을 입력하세요"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">담당자명</p>
                  <input
                    type="text"
                    value={centerForm.managerName}
                    onChange={(e) =>
                      handleInputChange('managerName', e.target.value)
                    }
                    placeholder="담당자명"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">담당자 이메일</p>
                  <input
                    type="email"
                    value={centerForm.managerEmail}
                    onChange={(e) =>
                      handleInputChange('managerEmail', e.target.value)
                    }
                    placeholder="Enter email"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">연락처</p>
                  <Select
                    value={selectedPhoneCode}
                    onChange={setSelectedPhoneCode}
                    data={countries
                      .filter(
                        (country) =>
                          country.dial_cd !== null &&
                          country.dial_cd !== undefined
                      )
                      .map((country) => ({
                        value: String(country.dial_cd),
                        label: String(country.dial_cd),
                      }))
                      .filter(
                        (item, index, self) =>
                          index ===
                          self.findIndex((t) => t.value === item.value)
                      )}
                    style={{ width: '120px' }}
                    placeholder="국가번호"
                  />
                  <input
                    type="text"
                    value={centerForm.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="연락처"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">아이디</p>
                  <input
                    type="text"
                    placeholder="아이디를 입력하세요"
                    value={centerForm.userID}
                    onChange={(e) =>
                      handleInputChange('userID', e.target.value)
                    }
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">비밀번호</p>
                  <PasswordInput
                    placeholder="비밀번호"
                    style={{ width: '100%' }}
                    value={centerForm.password}
                    onChange={(e) =>
                      handleInputChange('password', e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="general-info-wrapper">
            <div className="general-info-header-wrapper">
              <Icon name="info" size={20} />
              <p className="text-lg">위치 정보</p>
            </div>

            <div className="general-info-fields-wrapper">
              <div className="field-item">
                <p className="text-sm input-label">국가</p>
                <Select
                  value={centerForm.country}
                  onChange={handleSelectCountry}
                  data={countries.map((country) => ({
                    value: String(country.value),
                    label: String(country.label),
                  }))}
                  placeholder="Country"
                  width="200px"
                />
              </div>

              <div className="field-item">
                <p className="text-sm input-label">도시</p>
                <input
                  type="text"
                  value={centerForm.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City"
                />
              </div>

              <div className="field-item">
                <p className="text-sm input-label">Postal Code</p>
                <input
                  type="text"
                  value={centerForm.postcode}
                  onChange={(e) =>
                    handleInputChange('postcode', e.target.value)
                  }
                  placeholder="Postal Code"
                />
              </div>

              <div className="full-width-field">
                <p className="text-sm input-label">주소</p>
                <input
                  type="text"
                  value={centerForm.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Enter address"
                />
              </div>
            </div>
          </div>

          <div className="button-wrapper">
            <SaveButton onClick={handleCloseModal}>Cancel</SaveButton>
            <SaveButton onClick={handleAddCenter}>Submit</SaveButton>
          </div>
        </div>
      </Modal>

      <Modal
        opened={isDetailModalOpen}
        onClose={handleDetailModalClose}
        size="xl"
        title="Detail Information"
      >
        {selectedCenter && (
          <div className="modal-content">
            <div className="top-wrapper">
              <div className="level-info-wrapper">
                <div className="level-header-wrapper">
                  <Icon name="info" size={20} />
                  <p className="text-lg">담당자 정보</p>
                </div>

                <div className="info-inputs-wrapper">
                  <div className="field-item">
                    <label className="text-sm input-label">센터명</label>
                    <input
                      type="text"
                      value={detailForm.name}
                      onChange={(e) =>
                        handleDetailInputChange('name', e.target.value)
                      }
                      placeholder="센터명을 입력하세요"
                    />
                  </div>

                  <div className="field-item">
                    <p className="text-sm input-label">담당자명</p>
                    <input
                      type="text"
                      value={detailForm.managerName}
                      onChange={(e) =>
                        handleDetailInputChange('managerName', e.target.value)
                      }
                      placeholder="담당자명"
                    />
                  </div>

                  <div className="field-item">
                    <label className="text-sm input-label">담당자 이메일</label>
                    <input
                      type="email"
                      value={detailForm.managerEmail}
                      onChange={(e) =>
                        handleDetailInputChange('managerEmail', e.target.value)
                      }
                      placeholder="Enter email"
                    />
                  </div>

                  <div className="field-item">
                    <p className="text-sm input-label">연락처</p>
                    <Select
                      value={selectedDetailPhoneCode}
                      onChange={setSelectedDetailPhoneCode}
                      data={countries
                        .filter(
                          (country) =>
                            country.dial_cd !== null &&
                            country.dial_cd !== undefined
                        )
                        .map((country) => ({
                          value: String(country.dial_cd),
                          label: String(country.dial_cd),
                        }))
                        .filter(
                          (item, index, self) =>
                            index ===
                            self.findIndex((t) => t.value === item.value)
                        )}
                      style={{ width: '120px' }}
                      placeholder="국가번호"
                    />
                    <input
                      type="text"
                      value={detailForm.phone}
                      onChange={(e) =>
                        handleDetailInputChange('phone', e.target.value)
                      }
                      placeholder="연락처"
                    />
                  </div>

                  <div className="field-item">
                    <p className="text-sm input-label">아이디</p>
                    <input
                      type="text"
                      placeholder="아이디를 입력하세요"
                      value={detailForm.userID}
                      disabled
                    />
                  </div>

                  <div className="field-item">
                    <p className="text-sm input-label">비밀번호</p>
                    <PasswordInput
                      placeholder="비밀번호"
                      value={detailForm.password}
                      style={{
                        width: '100%',
                      }}
                      onChange={(e) =>
                        handleDetailInputChange('password', e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="general-info-wrapper">
              <div className="general-info-header-wrapper">
                <Icon name="info" size={20} />
                <p className="text-lg">위치 정보</p>
              </div>

              <div className="general-info-fields-wrapper">
                <div className="field-item">
                  <p className="text-sm input-label">국가</p>
                  <Select
                    value={detailForm.country}
                    onChange={handleDetailSelectCountry}
                    data={countries.map((country) => ({
                      value: String(country.value),
                      label: String(country.label),
                    }))}
                    placeholder="Country"
                    width="200px"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">도시</p>
                  <input
                    type="text"
                    value={detailForm.city}
                    onChange={(e) =>
                      handleDetailInputChange('city', e.target.value)
                    }
                    placeholder="City"
                  />
                </div>

                <div className="field-item">
                  <p className="text-sm input-label">Postal Code</p>
                  <input
                    type="text"
                    value={detailForm.postcode}
                    onChange={(e) =>
                      handleDetailInputChange('postcode', e.target.value)
                    }
                    placeholder="Postal Code"
                  />
                </div>

                <div className="full-width-field">
                  <p className="text-sm input-label">주소</p>
                  <input
                    type="text"
                    value={detailForm.address}
                    onChange={(e) =>
                      handleDetailInputChange('address', e.target.value)
                    }
                    placeholder="Enter address"
                  />
                </div>
              </div>
            </div>

            <div className="button-wrapper">
              <SaveButton onClick={handleUpdateCenter}>수정</SaveButton>
              <SaveButton onClick={handleDeleteCenter}>삭제</SaveButton>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CenterList
